
import type { DraftState } from '../../../../layout/sidebar/encounter-summary'
import type { ModalState } from '../delivery-history/delivery-history-form'
import type { ObstetricalState } from './types'

import { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { queryClient } from '../../../../../lib/query-client'
import { PlainButton } from '../../../../shared/Button'
import * as Card from '../../../../shared/card'
import { Button, DraftButton, PortedModal } from '../../../../shared/NarusMaterial'
import { ContextWarning, useWarning } from '../components/delivered-context'
import { Input } from '../components/input'

import {
  useObstetricalDeleteDraft,
  useObstetricalDraftMutation,
  useObstetricalHistory,
  useObstetricalSubmitMutation
} from './queries'
import { obstetricalSchema } from './types'

function debounce<T> (callback: (...args: T[]) => Promise<void>, delay: number): (...args: T[]) => Promise<void> {
  let timer: NodeJS.Timeout
  return async function (...rest) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      // eslint-disable-next-line n/no-callback-literal
      callback(...rest).catch((e) => console.error(e))
    }, delay)
  }
}

export function ObstetricalHistoryForm (): JSX.Element {
  const { id } = useParams()
  const { hide } = useWarning('obstetrical-history')

  const [ draft, setDraft ] = useState<DraftState>({ getting: true })

  const [ debouncedDraft ] = useDebounceValue(draft, 400)

  const obstetricalQuery = useObstetricalHistory(id, () => setDraft({ get: true }))
  const submitMutation = useObstetricalSubmitMutation(id, () => setDraft({ get: true }))
  const draftMutation = useObstetricalDraftMutation(id, () => setDraft({ set: true }))

  const saveDraft = useCallback(async (): Promise<void> => {
    setDraft({ setting: true })
    draftMutation.mutate(obstetricalQuery.data?.data ?? {})
  }, [ draftMutation.mutate, obstetricalQuery.data?.data ])

  const debouncedOnSubmit = debounce(saveDraft, 1000)

  function setData (values: Partial<ObstetricalState>): void {
    queryClient.setQueryData<{draft: true, data: Partial<ObstetricalState>}>([ 'obstetrical-history', id ?? '' ], (state) => {
      return {
        ...state,
        draft: true,
        data: {
          ...(state?.data ?? {}),
          ...values
        }
      }
    })
    void debouncedOnSubmit()
  }

  const onSubmit = useCallback((): void => {
    const parsed = obstetricalSchema.safeParse(obstetricalQuery.data?.data)
    if (parsed.success) {
      setDraft({ publishing: true })

      hide()
      submitMutation.mutate(parsed.data)
    }
  }, [ obstetricalQuery.data?.data, submitMutation.mutate, hide ])

  return (
    <div id='obstetrical-history-form'>
      <Card.Card>
        <Card.Header expandable>
          <h2>Obstetrical History</h2>
        </Card.Header>

        <Card.ExpandableWrapper>
          <Form
            data={obstetricalQuery.data?.data}
            setData={setData}
            isDraft={obstetricalQuery.data?.draft ?? false}
            saveDraft={debouncedOnSubmit}
            submit={onSubmit}
            draft={debouncedDraft}
            loading={!!draft.setting || !!draft.getting || !!draft.publishing}
          />
        </Card.ExpandableWrapper>
      </Card.Card>
    </div>
  )
}

interface FormProps {
  data?: ObstetricalState
  setData: (values: Partial<ObstetricalState>) => void
  isDraft: boolean
  submit: () => void
  saveDraft: () => Promise<void>
  draft: DraftState
  loading: boolean
}

export function Form ({ data, isDraft, submit, setData, draft, loading }: FormProps): JSX.Element {
  let [ modal, setModal ] = useState<ModalState>()
  let [ isValid, setIsValid ] = useState<boolean>()
  const { id } = useParams()

  const [ debouncedLoading ] = useDebounceValue(loading, 600)

  useEffect(() => {
    setIsValid(() => obstetricalSchema.safeParse(data).success)
  }, [ data ])

  const deleteDraftMutation = useObstetricalDeleteDraft(id)

  function onDiscard (): void {
    setModal({
      title: 'Are you sure you want to cancel all changes done on this form?',
      open: true,
      actions: [
        <Button
          key={1}
          data-cy='modal_cancel-btn'
          label='Cancel'
          // @ts-expect-error // It's fine
          onClick={() => {
            setModal(undefined)
          }}
          grey
        />,
        <Button
          key={2}
          label='Discard'
          data-cy='modal_discard-btn'
          // @ts-expect-error // It's fine
          onClick={() => {
            deleteDraftMutation.mutate(null)
            setModal(undefined)
          }}
        />
      ]
    })
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      className='px-8'
    >
      <ContextWarning
        contextKey='obstetrical-history'
        className='pt-4 pb-2'
      />

      <div
        className='flex flex-row gap-20 py-8 flex-wrap border-b border-x-0 border-t-0 border-solid border-gray-200'
      >
        <div className='flex flex-row gap-6'>
          <Input
            value={data?.gravida ?? ''}
            onChange={(e) => setData({ ...data, gravida: e.target.value })}
            title='Total number of pregnancies, regardless of the outcome.'
            label='Gravida'
            type='number'
            required
            min='1'
            inputStyle={{ maxWidth: '110px' }}
          />
          <Input
            value={data?.para ?? ''}
            onChange={(e) => setData({ ...data, para: e.target.value })}
            title='Total number of deliveries after 20 weeks of gestation, including both live births and stillbirths.'
            label='Para'
            type='number'
            required
            min='0'
            inputStyle={{ maxWidth: '110px' }}
          />
        </div>
        <div className='flex flex-row gap-6'>
          <Input
            value={data?.nvd ?? ''}
            onChange={(e) => setData({ ...data, nvd: e.target.value })}
            title='Number of deliveries via normal vaginal delivery '
            label='NVD'
            type='number'
            min='0'
            inputStyle={{ maxWidth: '110px' }}
          />
          <Input
            value={data?.cs ?? ''}
            onChange={(e) => setData({ ...data, cs: e.target.value })}
            label='CS'
            title='Number of deliveries via cesarean section.'
            type='number'
            min='0'
            inputStyle={{ maxWidth: '110px' }}
          />
          <Input
            value={data?.vbac ?? ''}
            onChange={(e) => setData({ ...data, vbac: e.target.value })}
            label='VBAC'
            title='Number of deliveries after a previous cesarean section, specifically through a vaginal birth after cesarean.'
            type='number'
            min='0'
            inputStyle={{ maxWidth: '110px' }}
          />
        </div>
      </div>

      <div className='flex flex-row gap-2 justify-end py-6 px-8'>
        <DraftButton draft={draft} />
        <PlainButton
          disabled={!isDraft || debouncedLoading}
          label='Discard Changes'
          type='button'
          onClick={onDiscard}
          data-cy='discard-btn'
        />
        <PlainButton
          disabled={!isDraft || debouncedLoading || !isValid}
          color='primary'
          btnType='contained'
          label='Publish'
          type='submit'
          data-cy='obstetrical-history-publish-btn'
        />
      </div>
      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </form>
  )
}
